import { useLayoutEffect, useRef, useState } from 'react'

import { debounce } from '@mui/material'

/**
 * Custom hook for measuring the width of a React component.
 * This hook uses a ResizeObserver to monitor changes in the width of the element `ref` is attached to.
 *
 * @returns An object containing:
 *  - ref: A React ref object to be attached to the component whose width you want to monitor.
 *  - width: The current width of the component.
 */
export const useWidth = () => {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number>(0)

  useLayoutEffect(() => {
    const handleResize = debounce((entries: ResizeObserverEntry[]) => {
      const rect = entries[0].contentRect

      setWidth(rect.width)
    }, 300)

    const observer = new ResizeObserver(handleResize)

    if (ref.current) {
      observer.observe(ref.current)

      return () => {
        observer.disconnect()
      }
    }

    return () => {
      observer.disconnect()
    }
  }, [ref])

  return { ref, width }
}
